import { nonBillableRoles } from "../TeamManagement/utils";

const getNumberOfNewPaidAccounts = (newUsers, existingUsers) => {
  const newPaidAccountsFromExistingUsers = existingUsers.filter(
    (user) => user.account?.accountType !== "unlimited",
  );

  return {
    newPaidAccountsFromExistingUsers:
      newPaidAccountsFromExistingUsers.length,
    netNewPaidAccounts: newUsers.length,
    totalNewPaidAccounts:
      newPaidAccountsFromExistingUsers.length + newUsers.length,
  };
};

export const getBillingIncrementText = (newUsers, existingUsers) => {
  const {
    newPaidAccountsFromExistingUsers,
    netNewPaidAccounts,
    totalNewPaidAccounts,
  } = getNumberOfNewPaidAccounts(newUsers, existingUsers);

  if (totalNewPaidAccounts === 0) {
    return "There are no billable changes. No additional charges will be incurred.";
  }

  return `Your billing will be incremented by ${totalNewPaidAccounts} subscription${
    totalNewPaidAccounts !== 1 ? "s" : ""
  }. This includes upgrading ${newPaidAccountsFromExistingUsers} existing free-plan account${
    newPaidAccountsFromExistingUsers !== 1 ? "s" : ""
  } and adding ${netNewPaidAccounts} new account${
    netNewPaidAccounts !== 1 ? "s" : ""
  }. This charge will be prorated based on the number of days remaining in the current billing cycle.
  If do not yet have any active subscriptions then these will be billed monthly at $99USD each.`;
};

export const getUserModificationBillingText = (user) => {
  const originalAccountType = user.originalAccountType;
  const originalIsBillable = !nonBillableRoles.includes(
    user.originalRoleId,
  );
  const newAccountType = user.accountType;
  const isBillable = !nonBillableRoles.includes(user.roleId);
  let billingImplication = 0;
  if (
    newAccountType === "unlimited" &&
    originalAccountType == "basic" &&
    isBillable
  ) {
    billingImplication = 1;
  } else if (
    newAccountType === "basic" &&
    originalAccountType == "unlimited" &&
    originalIsBillable
  ) {
    billingImplication = -1;
  } else if (
    newAccountType === "unlimited" &&
    originalAccountType == "unlimited" &&
    originalIsBillable &&
    !isBillable
  ) {
    billingImplication = -1;
  } else if (
    newAccountType === "unlimited" &&
    originalAccountType == "unlimited" &&
    isBillable &&
    !originalIsBillable
  ) {
    billingImplication = 1;
  }
  if (billingImplication === 0) {
    return "There are no billable changes. No additional charges will be incurred.";
  }
  return `Your billing will be ${
    billingImplication === 1 ? "incremented" : "decremented"
  } by 1 subscription. This charge will be prorated based on the number of days remaining in the current billing cycle.
 ${
   billingImplication === 1
     ? "If do not yet have any active subscriptions then you will be billed monthly for $99USD."
     : ""
 }`;
};

export const areThereAnyFailedUsers = (users) => {
  const existingUsers = users.existing;
  const newUsers = users.newUsers;

  const failedNewUsers = newUsers.filter((user) => !user.success);
  const failedExistingUsers = existingUsers.filter(
    (user) => !user.success,
  );

  if (
    failedNewUsers.length === 0 &&
    failedExistingUsers.length === 0
  ) {
    return false;
  }
  return true;
};

export const mapExistingUsersResponse = (existingUsersResponse) => {
  const mappedNewUsers = (existingUsersResponse?.newUsers || []).map(
    (email) => ({
      email,
      firstName: "",
      lastName: "",
      roleId: 1,
      isAdmin: false,
      accountType: "basic",
    }),
  );

  const mappedExistingUsers = (
    existingUsersResponse?.existingUsers || []
  ).map((user) => ({
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    roleId: user.roleId || 1,
    isAdmin: false,
    account: { accountType: user?.account?.accountType },
    accountType: user?.account?.accountType,
  }));

  const deactivatedUsers =
    existingUsersResponse?.deactivatedUsers || [];

  return {
    newUsersState: mappedNewUsers,
    existingUsersState: mappedExistingUsers,
    deactivatedUsers,
  };
};

export const validateUsers = ({
  newUsers,
  existingUsers,
  isOrg = false,
}) => {
  if (isOrg) {
    const allInputsFilled = [...newUsers, ...existingUsers].every(
      (u) => u.firstName && u.lastName && u.roleId && u.accountType,
    );
    return allInputsFilled
      ? { ok: true }
      : {
          ok: false,
          error:
            "All users must have a first name, last name, role, and account type.",
        };
  }

  const allInputsFilled = [...newUsers, ...existingUsers].every(
    (u) => u.roleId && u.accountType,
  );
  return allInputsFilled
    ? { ok: true }
    : {
        ok: false,
        error: "All users must have a role, and account type.",
      };
};

export const buildUsersInput = ({
  newUsers,
  existingUsers,
  teamUuid,
  createdAt,
  isOrg,
}) => {
  const newUserData = newUsers.map((user) =>
    isOrg
      ? {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          roleId: user.roleId,
          isAdmin: user.isAdmin,
          accountType: user.accountType,
        }
      : {
          email: user.email,
          roleId: user.roleId,
          isAdmin: user.isAdmin,
          accountType: user.accountType,
        },
  );

  const existingUserData = existingUsers.map((user) =>
    isOrg
      ? {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          roleId: user.roleId,
          isAdmin: user.isAdmin,
          accountType: user.accountType || "basic",
        }
      : {
          email: user.email,
          roleId: user.roleId,
          isAdmin: user.isAdmin,
          accountType: user.accountType,
        },
  );

  return {
    newUserData,
    existingUserData,
    teamUuid,
    createdAt,
  };
};
